import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import clsx from 'clsx';
import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { resetEditorImageColorAreasAndSync, setEditorImageColorAreaAndSync } from '../../Reducers/Configurator/Actions';
import {
  getOptionClassificationTitle,
  getOptionsForClassification,
  getSelectedOptionForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { AttributeIdentifiers } from '../../Enums';
import { formatHexColor } from '../../utils/editorUnits';
import { filterColorOptions, getOptionAttrValues } from '../../utils/colorOptionFilter';
import type { ColorArea, ImageItem } from '../../Reducers/Configurator/types';
import { XXL_COLORS } from '../../theme/colors';
import OptionTiles from './OptionTiles';
import SectionTitle from './SectionTitle';

const COLOR_SWATCH_SIZE = 36;

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    section: {
      marginBottom: theme.spacing(2),
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    resetButton: {
      flexShrink: 0,
      padding: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: 11,
      fontWeight: 600,
      color: tokens.accent,
      textDecoration: 'underline',
      textDecorationStyle: 'dotted',
      letterSpacing: '0.02em',
      '&:hover': {
        color: tokens.sectionTitle,
      },
    },
    areaList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
      marginBottom: theme.spacing(1.5),
    },
    areaTile: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      width: '100%',
      padding: theme.spacing(1, 1.25),
      border: `1px solid ${tokens.borderStrong}`,
      backgroundColor: tokens.paperBackground,
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'border-color 0.12s ease, background-color 0.12s ease',
      '&:hover': {
        borderColor: tokens.accent,
      },
    },
    areaTileActive: {
      borderColor: tokens.tileSelectedBorder,
      backgroundColor: tokens.tileSelectedBg,
      boxShadow: `inset 0 0 0 1px ${tokens.tileSelectedBorder}`,
    },
    areaSwatch: {
      width: COLOR_SWATCH_SIZE,
      height: COLOR_SWATCH_SIZE,
      flexShrink: 0,
      border: `1px solid ${tokens.borderStrong}`,
    },
    areaLabel: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 600,
      color: tokens.sectionTitle,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    pickerSection: {
      marginTop: theme.spacing(0.5),
    },
    checkIcon: {
      color: tokens.accent,
      fontSize: 18,
      flexShrink: 0,
    },
  };
});

type Props = {
  item: ImageItem;
};

export default function ImageColorAreasSection({ item }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const colorOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.color));
  const colorTitle = useSelector(getOptionClassificationTitle(OptionClassificationIdentifiers.color));
  const materialOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.material));
  const materialSelected = useSelector(
    getSelectedOptionForClassification(OptionClassificationIdentifiers.material)
  );
  const selectedThickness = React.useMemo(() => {
    const id = materialSelected?.identifier;
    if (!id) return null;
    const opt = materialOptions.find((o) => o.identifier === id);
    return opt ? (getOptionAttrValues(opt, AttributeIdentifiers.thickness)[0] ?? null) : null;
  }, [materialSelected?.identifier, materialOptions]);
  const [activeAreaIndex, setActiveAreaIndex] = React.useState<number | null>(null);

  const colorAreas = item.colorAreas || [];
  const colorAreaCount = colorAreas.length;
  // Geometry/count only — not selected colors — so picking a swatch keeps the area active.
  const colorAreasStructureKey = [
    colorAreaCount,
    ...(item.colorAreaBounds?.map(
      (bounds) =>
        `${Math.round(bounds.x)}:${Math.round(bounds.y)}:${Math.round(bounds.width)}:${Math.round(bounds.height)}`
    ) ?? []),
  ].join('|');
  const itemSurface = item.selectedOptions?.[OptionClassificationIdentifiers.surface];

  React.useEffect(() => {
    setActiveAreaIndex(null);
  }, [item.id, item.url, colorAreasStructureKey]);

  React.useEffect(() => {
    setActiveAreaIndex((current) =>
      current != null && current >= colorAreaCount ? null : current
    );
  }, [colorAreaCount]);

  const hasChanges = React.useMemo(() => {
    const initial = item.initialColorAreas;
    if (!initial || initial.length === 0 || colorAreas.length !== initial.length) return false;
    return colorAreas.some((area, i) => area.colorOptionId !== initial[i].colorOptionId);
  }, [colorAreas, item.initialColorAreas]);

  const filteredOptions = React.useMemo(() => {
    return filterColorOptions(colorOptions, {
      surface: itemSurface ?? null,
      thickness: selectedThickness,
    });
  }, [colorOptions, itemSurface, selectedThickness]);

  const optionTitleById = React.useMemo(() => {
    const map = new Map<string, string>();
    colorOptions.forEach((option) => {
      if (option.identifier) map.set(option.identifier, option.title || option.identifier);
    });
    return map;
  }, [colorOptions]);

  if (colorAreas.length === 0) return null;

  const handleAreaClick = (index: number) => {
    setActiveAreaIndex((current) => (current === index ? null : index));
  };

  const handleColorSelect = (option: { identifier: string }) => {
    if (activeAreaIndex == null) return;
    const hex = formatHexColor(
      getItemAttributeValueByIdentifier(option, AttributeIdentifiers.hex, '000000')
    );
    dispatch(setEditorImageColorAreaAndSync(item.id, activeAreaIndex, hex, option.identifier));
  };

  const handleReset = () => {
    dispatch(resetEditorImageColorAreasAndSync(item.id));
  };

  const activeArea = activeAreaIndex != null ? colorAreas[activeAreaIndex] : null;

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <SectionTitle>{colorTitle}</SectionTitle>
        {hasChanges && (
          <button type="button" className={classes.resetButton} onClick={handleReset}>
            {t('imageItemCard.resetColors')}
          </button>
        )}
      </div>
      <div className={classes.areaList}>
        {colorAreas.map((area: ColorArea, index) => {
          const isActive = activeAreaIndex === index;
          const label = optionTitleById.get(area.colorOptionId) || area.colorOptionId;
          return (
            <button
              key={`${item.id}-area-${index}`}
              type="button"
              className={clsx(classes.areaTile, isActive && classes.areaTileActive)}
              onClick={() => handleAreaClick(index)}
            >
              <span className={classes.areaSwatch} style={{ backgroundColor: area.color }} aria-hidden />
              <span className={classes.areaLabel}>{label}</span>
              {isActive ? <Check className={classes.checkIcon} /> : null}
            </button>
          );
        })}
      </div>

      {activeArea ? (
        <div className={classes.pickerSection}>
          <OptionTiles
            title=""
            options={filteredOptions}
            selectedIdentifier={activeArea.colorOptionId}
            onSelect={handleColorSelect}
            showColorSwatch
          />
        </div>
      ) : null}
    </div>
  );
}
